import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, XCircle, User, BookOpen, Target, Palette, CheckCircle, Crop, Smartphone } from 'lucide-react';
import LoadingSpinner from './ui/LoadingSpinner';
import { buttonClasses } from './ui/Button';
import FormField from './ui/FormField.jsx';
import { Input, Textarea } from './ui/Input.jsx';
import ImageCropModal from './ImageCropModal';
import UnifiedSkillSelector from './UnifiedSkillSelector';
import TimeSelector from './TimeSelector';
import { useAuth } from '../contexts/AuthContext';
import { SharedStyles } from '../styles/SharedStyles';
import AuraLightBackground from './ui/AuraLightBackground';
import AppStoreButtons from './ui/AppStoreButtons';
import SessionJoinSuccess from './session/SessionJoinSuccess';
import { authorizedFetch, parseJsonResponse, resolveAssetUrl } from '../utils/api';
import { isNativeApp } from '../utils/deviceDetection';
import { scrollWindowToTop } from '../utils/scrollPosition';
import Avatar from './ui/Avatar.jsx';

const ProfileSetup = ({ onComplete, onFinish }) => {
  const { user, logout } = useAuth();

  // Ausgang aus dem Onboarding: Das Gate in App.js leitet JEDE Route auf /profile-setup,
  // solange das Profil unvollständig ist — auch /login. Ohne diesen Knopf kam ein Nutzer,
  // bei dem das Speichern dauerhaft scheitert (oder der das falsche Konto erwischt hat),
  // nur per App-Neuinstallation wieder raus. Harter Reload, damit kein Setup-State überlebt.
  const handleLogoutFromSetup = () => {
    try {
      logout();
    } finally {
      window.location.href = '/login';
    }
  };

  // Entwurfs-Speicher: Eingaben werden lokal im Browser zwischengespeichert, damit bei
  // Abbruch/Reload/App-Wechsel nichts verloren geht. Pro User getrennt (kein fremder Entwurf).
  const draftKey = `eachotter_onboarding_draft_${user?.id || user?.email || 'current'}`;

  const [step, setStep] = useState(0); // Start mit Welcome-Screen
  const [profile, setProfile] = useState(() => {
    const base = {
      name: user?.name || '',
      grade: '',
      age: '',
      aboutMe: '', // Bio für Lernpersönlichkeit
      image: null, // Einzelnes Profilbild (statt images Array)
      offers: [],
      seeks: [],
      availability: {} // Verfügbarkeitszeiten
    };
    // Gespeicherten Entwurf laden (Bild wird NICHT aus dem Entwurf übernommen –
    // die Vorschau-URL wäre nach einem Reload ohnehin ungültig).
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        return { ...base, ...JSON.parse(saved), image: null };
      }
    } catch (e) {
      // Privater Modus / Speicher voll: einfach mit leerem Profil starten.
    }
    return base;
  });
  const [imageFile, setImageFile] = useState(null); // Store actual file object (cropped Blob)
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const [cropImageSrc, setCropImageSrc] = useState(null); // Data-URL fürs Crop-Modal
  const [showCropModal, setShowCropModal] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false); // Abschluss-Overlay "Schön, dass du dabei bist!"

  useEffect(() => {
    // Aktualisiere Profile, wenn user sich ändert
    if (user) {
      setProfile(currentProfile => ({
        ...currentProfile,
        name: user.name || currentProfile.name
      }));
    }
  }, [user]);

  // Auto-Speichern: Bei jeder Änderung den Entwurf lokal sichern (ohne Bild – nicht serialisierbar).
  // So bleiben die Eingaben erhalten, wenn der/die Lernende zwischendurch aussteigt.
  useEffect(() => {
    try {
      const { image, ...persistable } = profile;
      localStorage.setItem(draftKey, JSON.stringify(persistable));
    } catch (e) {
      // Speicher nicht verfügbar: Auto-Speichern überspringen, Flow läuft normal weiter.
    }
  }, [profile, draftKey]);

  // Entwurf löschen – erst nach erfolgreichem Speichern in der Datenbank aufrufen.
  const clearDraft = () => {
    try {
      localStorage.removeItem(draftKey);
    } catch (e) {
      // ignorieren
    }
  };

  // Bei jedem Schrittwechsel nach oben scrollen, damit jede Seite (insb. Step 3
  // mit Profilbild oben) sichtbar am Seitenanfang beginnt – nicht mittig auf Mobile.
  // Zweiter Aufruf via requestAnimationFrame: Framer Motion rendert den neuen Step
  // erst nach dem ersten Tick, der erste Scroll läuft sonst ins noch nicht finale Layout.
  useEffect(() => {
    scrollWindowToTop('auto');
    const raf = requestAnimationFrame(() => scrollWindowToTop('auto'));
    return () => cancelAnimationFrame(raf);
  }, [step]);

  // Schritt 1: Datei auswählen → Crop-Modal öffnen
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Dateigröße prüfen (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setImageError('Die Datei ist zu groß. Maximal 5MB erlaubt.');
      return;
    }

    setImageError('');

    // Als Data-URL lesen und Crop-Modal öffnen
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);

    // File-Input zurücksetzen, damit dieselbe Datei erneut gewählt werden kann
    e.target.value = '';
  };

  // Schritt 2: Zugeschnittenes Bild übernehmen (Upload erfolgt erst beim Abschluss in handleNext)
  const handleCropComplete = (croppedBlob) => {
    setShowCropModal(false);
    setCropImageSrc(null);

    if (!(croppedBlob instanceof Blob) || croppedBlob.size === 0) {
      setImageError('Bild konnte nicht verarbeitet werden. Bitte wähle den Ausschnitt erneut.');
      return;
    }

    setImageFile(croppedBlob);
    const previewUrl = URL.createObjectURL(croppedBlob);
    setProfile(prev => ({ ...prev, image: previewUrl }));
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setCropImageSrc(null);
  };

  // Bestehende Auswahl erneut zuschneiden
  const handleEditExistingImage = () => {
    if (profile.image) {
      setCropImageSrc(profile.image);
      setShowCropModal(true);
    }
  };

  const handleRemoveImage = () => {
    setProfile(prev => ({ ...prev, image: null }));
    setImageFile(null);
  };

  const handleNext = async () => {
    if (step === 3) {
      // Während Upload + Speichern bleibt der Button im Lade-Zustand ("Profil wird erstellt...").
      setUploadingImage(true);

      // 1) Optionales Profilbild hochladen
      let finalProfile = { ...profile };

      if (imageFile) {
        try {
          const formData = new FormData();
          formData.append('avatar', imageFile, 'avatar.jpg');

          const response = await authorizedFetch('/api/v1/profile/avatar', {
            method: 'POST',
            body: formData
          });
          const data = await parseJsonResponse(response, { allowPathFallback: true });

          if (response.ok) {
            // Update profile with uploaded image URL
            const uploadedImage = data.avatarUrl || data.avatar || data.url;
            if (uploadedImage) {
              finalProfile.image = resolveAssetUrl(uploadedImage);
            }
          } else {
            console.error('Failed to upload profile image:', data.error || response.status);
            // Continue without image
          }
        } catch (error) {
          console.error('Error uploading profile image:', error);
          // Continue without image
        }
      }

      setProfile(finalProfile);

      // 2) Profil JETZT verbindlich speichern – vor der Erfolgsseite mit dem App-Download-Hinweis.
      //    So ist das Profil sicher in der DB; steigt der/die Lernende danach über den
      //    Download-Link aus, geht nichts verloren.
      try {
        await onComplete(finalProfile);
        clearDraft(); // Entwurf erst nach erfolgreichem Speichern entfernen
        setStep(4);
      } catch (error) {
        // Speichern fehlgeschlagen: auf Schritt 3 bleiben (Eingaben + Entwurf bleiben erhalten),
        // der Fehler-Toast kommt aus App.js.
      } finally {
        setUploadingImage(false);
      }
    } else if (step === 4) {
      // Erfolgsseite: "Los geht's!" -> Abschluss-Overlay zeigen.
      // Das Profil ist bereits gespeichert; das Overlay leitet via onFinish weiter.
      setShowWelcome(true);
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step === 4) return; // Kein Zurück von der Erfolgsseite
    setStep(step - 1);
  };

  const isStepValid = () => {
    switch (step) {
      case 0:
        // Welcome-Screen ist immer gültig
        return true;
      case 1:
        return (
          profile.name && profile.name.trim() !== '' &&
          profile.grade !== ''
        );
      case 2:
        // UND, nicht ODER: Das Backend wertet ein Profil erst als vollstaendig, wenn
        // beide Listen gefuellt sind (auth.js /me: offered && sought). Mit ODER kam man
        // hier durch, bekam von /me aber dauerhaft requiresProfileSetup:true - und landete
        // bei jedem Login wieder im Onboarding.
        return profile.offers.length > 0 && profile.seeks.length > 0;
      case 3:
        // Verfügbarkeit ist optional - Schritt ist immer gültig
        return true;
      case 4:
        // Erfolgsseite ist immer gültig
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen relative">
      <AuraLightBackground intensity="normal" showAnimations={true} />
      <div className="relative z-10 max-w-2xl mx-auto p-3 sm:p-6">
        <div className="bg-surface/80 backdrop-blur-sm rounded-3xl shadow-token-xl p-4 sm:p-8 border border-border">
        {/* Notausgang: Abmelden ist die einzige Route aus dem Onboarding heraus */}
        <div className="flex justify-end -mt-1 mb-2">
          <button
            type="button"
            onClick={handleLogoutFromSetup}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline min-h-[44px] px-2"
            aria-label="Abmelden und zum Login"
          >
            Abmelden
          </button>
        </div>
        {/* Progress Bar - nur anzeigen wenn nicht auf Welcome-Screen */}
        {step > 0 && (
          <div className="mb-8">
            <div className="h-2 bg-surface-raised rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(Math.min(step, 4) / 4) * 100}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span className={step >= 1 ? 'text-primary font-medium' : ''}>Basics</span>
              <span className={step >= 2 ? 'text-primary font-medium' : ''}>Skills</span>
              <span className={step >= 3 ? 'text-primary font-medium' : ''}>Profil</span>
              <span className={step >= 4 ? 'text-success font-medium' : ''}>Fertig!</span>
            </div>
          </div>
        )}

        {/* Step Content */}
        <motion.div
          key={step}
          initial={{ opacity: 0, x: step === 0 ? 0 : 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6"
        >
          {/* Step 0: Welcome Screen */}
          {step === 0 && (
            <div className="text-center space-y-6 py-8">
              <motion.div 
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 3,
                  ease: "easeInOut"
                }}
                className="text-8xl inline-block"
              >
                🦦
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl font-bold text-foreground"
              >
                Willkommen bei EachOtter!
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-muted-foreground text-lg max-w-md mx-auto"
              >
                In nur <strong>3 kurzen Schritten</strong> findest du passende Lernpartner:innen.
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex flex-col items-center gap-4 pt-4"
              >
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="text-muted-foreground">①</span> Basics</span>
                  <span>→</span>
                  <span className="flex items-center gap-1"><span className="text-info">②</span> Skills</span>
                  <span>→</span>
                  <span className="flex items-center gap-1"><span className="text-success">③</span> Profil</span>
                </div>
              </motion.div>
            </div>
          )}

          {step === 1 && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-foreground/5 rounded-xl">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Erzähl von dir!</h2>
              </div>
              <p className="text-muted-foreground mb-6">Damit das Matching dich mit den passenden Lernpartner:innen zusammenbringt.</p>

              <div className="space-y-4">
                <FormField
                  label={
                    <span className="flex items-center gap-2">
                      <User className="h-4 w-4 text-primary" />
                      Dein Name
                    </span>
                  }
                >
                  <Input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder="Dein Name"
                  />
                </FormField>

                {/* Alter wird im Onboarding nicht mehr abgefragt (Sep 2026): Es fliesst weder
                    ins Matching noch in die Forschungsdaten ein, und die Zulassung Minderjaehriger
                    laeuft ueber den Einladungscode der Schule - nicht ueber eine Selbstangabe.
                    Optional weiterhin in ProfileEditPage pflegbar. */}
                <FormField
                  label={
                    <span className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-muted-foreground" />
                      In welcher Klasse bist du?
                    </span>
                  }
                  hint="Gib deine Klasse oder deinen Jahrgang ein"
                >
                  <Input
                    type="text"
                    value={profile.grade}
                    onChange={(e) => setProfile({ ...profile, grade: e.target.value })}
                    placeholder="z.B. IT643 oder 10b"
                  />
                </FormField>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-info-soft rounded-xl">
                  <Target className="h-6 w-6 text-info" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Deine Skills</h2>
              </div>
              <p className="text-muted-foreground mb-6">
                Wähle aus, wobei du helfen kannst und was du lernen möchtest.
                Für das Matching brauchst du <strong>mindestens eine Stärke und ein Lernziel</strong>.
              </p>
              <UnifiedSkillSelector
                offeredSkills={profile.offers}
                soughtSkills={profile.seeks}
                onOfferedSkillsChange={(skills) => setProfile(prev => ({ ...prev, offers: skills }))}
                onSoughtSkillsChange={(skills) => setProfile(prev => ({ ...prev, seeks: skills }))}
              />
              {/* Erfolgs-Feedback wenn beide Listen gefüllt sind – sonst sagen, was noch fehlt */}
              {profile.offers.length > 0 && profile.seeks.length > 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 bg-success-soft rounded-xl border border-success/30 text-center"
                >
                  <p className="text-sm text-foreground/80">
                    <strong>Super!</strong> {profile.offers.length} {profile.offers.length === 1 ? 'Stärke' : 'Stärken'} und {profile.seeks.length} {profile.seeks.length === 1 ? 'Lernziel' : 'Lernziele'} ausgewählt!
                  </p>
                </motion.div>
              ) : (profile.offers.length > 0 || profile.seeks.length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-3 bg-info-soft rounded-xl border border-info/30 text-center"
                >
                  <p className="text-sm text-foreground/80">
                    {profile.seeks.length === 0
                      ? 'Fehlt noch: Wähle mindestens ein Lernziel – etwas, das du lernen möchtest.'
                      : 'Fehlt noch: Wähle mindestens eine Stärke – etwas, wobei du helfen kannst.'}
                  </p>
                </motion.div>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-foreground/5 rounded-xl">
                  <Palette className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Vervollständige dein Profil</h2>
              </div>
              <p className="text-muted-foreground mb-6">Füge ein Profilbild und eine kurze Beschreibung hinzu, damit andere dich besser kennenlernen können.</p>

              <div className="space-y-6">
                {/* Profilbild Upload */}
                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-3">Profilbild (optional)</label>
                  <div className="flex flex-col items-center">
                    <div className="relative w-32 h-32 mb-4">
                      <div
                        className="w-full h-full bg-foreground/5 rounded-full overflow-hidden"
                        style={{ boxShadow: SharedStyles.glassShadows.cardLift }}
                      >
                        {profile.image ? (
                          <img
                            src={profile.image}
                            alt="Profilbild"
                            className="w-full h-full object-cover"
                            style={{ objectPosition: 'center 25%' }}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                            <Plus size={24} className="mb-1" />
                            <p className="text-xs text-center">Bild<br/>hinzufügen</p>
                          </div>
                        )}
                      </div>
                      {profile.image && (
                        <div className="absolute bottom-0 right-0 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleEditExistingImage}
                            className="h-10 w-10 rounded-full bg-surface text-foreground/80 hover:bg-surface-raised transition-colors shadow-token-md flex items-center justify-center"
                            aria-label="Bild zuschneiden & drehen"
                            title="Zuschneiden & Drehen"
                          >
                            <Crop size={16} className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="h-10 w-10 rounded-full bg-danger text-danger-foreground hover:opacity-90 transition-colors shadow-token-md flex items-center justify-center"
                            aria-label="Bild löschen"
                          >
                            <XCircle size={16} className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    <label
                      htmlFor="image-upload" 
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer transition-all ${ 
                        uploadingImage
                          ? 'bg-surface-raised text-muted-foreground cursor-not-allowed'
                          : 'bg-primary text-primary-foreground hover:bg-primary-hover shadow-token-sm'
                      }`}
                    >
                      {uploadingImage ? (
                        <>
                          <LoadingSpinner size="small" />
                          Lädt...
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          {profile.image ? 'Ändern' : 'Hochladen'}
                        </>
                      )}
                    </label>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    
                    {imageError && (
                      <div role="alert" className="mt-2 p-2 bg-danger-soft text-foreground rounded-xl text-sm text-center">
                        {imageError}
                      </div>
                    )}
                  </div>
                </div>

                {/* Bio/Über mich */}
                <div>
                  <FormField
                    label={
                      <span className="flex items-center gap-2">
                        <Palette className="h-4 w-4 text-primary" />
                        Beschreibe deine Lernpersönlichkeit (optional)
                      </span>
                    }
                  >
                    <Textarea
                      value={profile.aboutMe}
                      onChange={(e) => setProfile({ ...profile, aboutMe: e.target.value })}
                      placeholder="Ich lerne am besten, wenn..."
                      rows={3}
                      maxLength={160}
                    />
                  </FormField>
                  <div className="flex justify-between mt-1">
                    <p className={SharedStyles.textCaption}>
                      {profile.aboutMe.length}/160 Zeichen
                    </p>
                  </div>
                </div>

                {/* Verfügbarkeit */}
                <div>
                  <h3 className="text-sm font-medium text-foreground/80 mb-2">Wann hast du Zeit? (optional)</h3>
                  <p className="text-xs text-muted-foreground mb-3">Du kannst das auch später in den Einstellungen anpassen.</p>
                  <TimeSelector
                    selectedTimes={profile.availability}
                    onChange={(times) => setProfile({ ...profile, availability: times })}
                  />
                </div>
              </div>
            </>
          )}
          {/* Step 4: Erfolgsseite */}
          {step === 4 && (
            <div className="text-center space-y-6 py-6">
              {/* Erfolgs-Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success-soft mb-2">
                <CheckCircle className="h-10 w-10 text-success" />
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                Profil erstellt!
              </h2>

              {/* Mini Profilcard Vorschau */}
              <div
                className="bg-surface rounded-xl p-4 sm:p-6 max-w-sm mx-auto text-left"
                style={{ boxShadow: SharedStyles.glassShadows.cardLift }}
              >
                <div className="flex items-center gap-4 mb-4">
                  {/* Avatar */}
                  <div className="w-16 h-16 flex-shrink-0">
                    {/* Platzhalter und Rahmen kommen aus ui/Avatar */}
                    <Avatar
                      name={profile.name}
                      src={profile.image}
                      size="fill"
                      style={{ objectPosition: 'center 25%' }}
                    />
                  </div>
                  {/* Name und Klasse */}
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{profile.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {profile.grade && `Klasse ${profile.grade}`}
                    </p>
                  </div>
                </div>

                {/* Skills Vorschau */}
                {profile.offers.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Kann helfen bei:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.offers.slice(0, 4).map((skill, i) => (
                        <span key={i} className="px-2.5 py-1 bg-offer-soft text-foreground rounded-full text-xs font-medium" style={{ boxShadow: SharedStyles.glassShadows.emeraldHairline }}>
                          {typeof skill === 'object' ? skill.name : skill}
                        </span>
                      ))}
                      {profile.offers.length > 4 && (
                        <span className="px-2.5 py-1 bg-surface-raised text-muted-foreground rounded-full text-xs font-medium">
                          +{profile.offers.length - 4}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {profile.seeks.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">Möchte lernen:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.seeks.slice(0, 4).map((skill, i) => (
                        <span key={i} className="px-2.5 py-1 bg-primary-soft text-foreground rounded-full text-xs font-medium" style={{ boxShadow: SharedStyles.glassShadows.violetHairline }}>
                          {typeof skill === 'object' ? skill.name : skill}
                        </span>
                      ))}
                      {profile.seeks.length > 4 && (
                        <span className="px-2.5 py-1 bg-surface-raised text-muted-foreground rounded-full text-xs font-medium">
                          +{profile.seeks.length - 4}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Motivationstext */}
              <p className="text-base text-muted-foreground leading-relaxed max-w-md mx-auto">
                Lerne andere Schülerinnen und Schüler kennen, starte Lernsessions, bekomme Aurapunkte und halte deine Lernerfolge fest.
              </p>

              {/* App-Download-Hinweis – nur außerhalb der nativen App anzeigen */}
              {!isNativeApp() && (
                <div className="bg-surface-raised rounded-xl p-4 sm:p-5 max-w-sm mx-auto border border-border">
                  <p className="text-sm font-semibold text-foreground mb-1 flex items-center gap-1.5">
                    <Smartphone className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Für die beste Erfahrung
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">Hol dir die App für iOS &amp; Android.</p>
                  {/* Neuer Tab: Die Erfolgsseite mit „Los geht's!“ bleibt stehen. */}
                  <AppStoreButtons internalInNewTab />
                </div>
              )}

              {/* Otter Emoji */}
              <div className="text-5xl animate-bounce">🦦</div>
            </div>
          )}
        </motion.div>

        {/* Navigation Buttons */}
        {step < 4 ? (
          <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 mt-8">
            {step > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-surface-raised text-foreground hover:bg-foreground/5 transition-all duration-200"
              >
                <ChevronLeft size={20} />
                Zurück
              </button>
            ) : (
              <div></div>
            )}

            <motion.button
              onClick={handleNext}
              disabled={!isStepValid() || uploadingImage}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={buttonClasses('primary', step === 0 ? 'lg' : 'md', 'whitespace-nowrap')}
            >
              {step === 0 && "Los geht's!"}
              {step > 0 && step < 3 && (
                <>
                  Weiter
                  <ChevronRight size={20} />
                </>
              )}
              {step === 3 && (uploadingImage ? (
                <>
                  <LoadingSpinner size="small" />
                  Profil wird erstellt...
                </>
              ) : "Profil erstellen")}
            </motion.button>
          </div>
        ) : (
          /* Step 4: Erfolgsseite - zentrierter Button */
          <div className="flex justify-center mt-8">
            <motion.button
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={buttonClasses('primary', 'lg', 'whitespace-nowrap')}
            >
              Los geht&apos;s!
            </motion.button>
          </div>
        )}
        </div>
      </div>

      {/* Crop-Modal: runder Zuschnitt wie in ProfileEditPage */}
      {showCropModal && cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={1}
          cropShape="round"
        />
      )}

      {/* Abschluss-Overlay: hüpfender Otter, gleiche Optik wie die Session-Beitritt-Bestätigung */}
      {showWelcome && (
        <SessionJoinSuccess
          title="Schön, dass du dabei bist!"
          message="Los geht's mit EachOtter"
          onDone={() => {
            if (typeof onFinish === 'function') {
              onFinish();
            } else {
              setShowWelcome(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default ProfileSetup;
